import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton, Modal, TextLink } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { EXTENSION_ID } from "app/pages/sales-intelligence/constants/common";
import { useCopiedNotification } from "app/pages/sales-intelligence/hooks/useCopiedNotification";
import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";

const GOOGLE_ADMIN_CONSOLE_URL = "https://admin.google.com";
const MICROSOFT_INTUNE_URL = "https://intune.microsoft.com";

type ITInstructionsModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export const ITInstructionsModal = ({ isOpen, onClose }: ITInstructionsModalProps) => {
    const { translate } = useTranslate();

    const { isCopied, onCopy } = useCopiedNotification();

    const handleCopyInstructions = useCallback(() => {
        extensionManagementTrackingService.trackItInstructionsCopy();
        const text = [
            translate("si.management.extension.deployment.self_install.it_team.line1"),
            `${translate("si.management.extension.deployment.self_install.it_team.line2_prefix")} ${EXTENSION_ID} ${translate("si.management.extension.deployment.self_install.it_team.line2_middle")} ExtensionInstallForcelist"`,
            translate("si.management.extension.deployment.self_install.it_team.line3"),
        ].join("\n");

        void navigator.clipboard
            .writeText(text)
            .then(onCopy)
            .catch((error: Error) => {
                Sentry.errorTracker({
                    e: error,
                    message: "Failed to copy IT instructions to clipboard",
                    level: "warning",
                });
            });
    }, [translate, onCopy]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            data-automation="extension-deployment-it-instructions-modal"
            customStyles={{
                content: {
                    width: "520px",
                    maxWidth: "520px",
                },
            }}
        >
            <Modal.Header data-automation="extension-deployment-it-instructions-modal-header">
                <Modal.Title>
                    {translate("si.management.extension.deployment.self_install.view_it_instructions")}
                </Modal.Title>
            </Modal.Header>
            <Modal.CloseButton
                onClick={onClose}
                data-automation="extension-deployment-it-instructions-modal-close"
            />
            <Modal.Content>
                <Stack
                    padding="16px 20px"
                    borderRadius="8px"
                    background={colorsPalettes.bluegrey["200"]}
                    gap="12px"
                    alignItems="flex-start"
                    data-automation="extension-deployment-it-instructions-content"
                    fontVariant="body2"
                >
                    <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Text
                            as="strong"
                            fontVariant="subTitle3"
                            color={colorsPalettes.carbon["500"]}
                        >
                            {translate("si.management.extension.deployment.self_install.it_team.title")}
                        </Text>
                        <IconButton
                            type="flat"
                            iconName={isCopied ? "checked" : "copy-rounded"}
                            iconSize="xs"
                            onClick={handleCopyInstructions}
                            data-automation="extension-deployment-it-instructions-copy"
                        />
                    </Flex>
                    <Stack
                        gap="4px"
                        paddingX="16px"
                    >
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {translate("si.management.extension.deployment.self_install.it_team.line1")}
                        </Text>
                        <Flex
                            gap="4px"
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon["400"]}
                            >
                                {translate("si.management.extension.deployment.self_install.it_team.line2_prefix")}
                            </Text>
                            <Text
                                fontVariant="caption"
                                padding="2px 8px"
                                borderRadius="4px"
                                background={colorsPalettes.carbon["0"]}
                                border={`1px solid ${colorsPalettes.carbon["50"]}`}
                                color={colorsPalettes.carbon["500"]}
                            >
                                {EXTENSION_ID}
                            </Text>
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon["400"]}
                            >
                                {translate("si.management.extension.deployment.self_install.it_team.line2_middle")}
                            </Text>
                            <Text
                                fontVariant="caption"
                                padding="2px 8px"
                                borderRadius="4px"
                                background={colorsPalettes.carbon["0"]}
                                border={`1px solid ${colorsPalettes.carbon["50"]}`}
                                color={colorsPalettes.carbon["500"]}
                            >
                                ExtensionInstallForcelist
                            </Text>
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon["400"]}
                            >
                                "
                            </Text>
                        </Flex>
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon["400"]}
                        >
                            {translate("si.management.extension.deployment.self_install.it_team.line3")}
                        </Text>
                    </Stack>
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon["400"]}
                    >
                        {translate("si.management.extension.deployment.self_install.it_team.method_prefix")}{" "}
                        <TextLink
                            href={GOOGLE_ADMIN_CONSOLE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-automation="extension-deployment-google-admin-link"
                            translations={{ opensInNewTab: "" }}
                            onClick={() => extensionManagementTrackingService.trackItInstructionsGoogleAdminLink()}
                        >
                            {translate("si.management.extension.deployment.self_install.it_team.google_admin")}
                        </TextLink>{" "}
                        {translate("si.management.extension.deployment.self_install.it_team.method_middle")}{" "}
                        <TextLink
                            href={MICROSOFT_INTUNE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-automation="extension-deployment-intune-link"
                            translations={{ opensInNewTab: "" }}
                            onClick={() => extensionManagementTrackingService.trackItInstructionsIntuneLink()}
                        >
                            {translate("si.management.extension.deployment.self_install.it_team.intune")}
                        </TextLink>{" "}
                        {translate("si.management.extension.deployment.self_install.it_team.method_suffix")}
                    </Text>
                </Stack>
            </Modal.Content>
            <Modal.Footer data-automation="extension-deployment-it-instructions-modal-footer">
                <Button
                    type="primary"
                    onClick={() => {
                        extensionManagementTrackingService.trackItInstructionsGotIt();
                        onClose();
                    }}
                    data-automation="extension-deployment-it-instructions-modal-got-it"
                >
                    {translate("si.common.got-it")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
